/**
 * The worklist as rows with countdowns (4.2, 5.3): SOC due, recert window,
 * supervisory due, frequency shortfall, visits at risk, no reply,
 * availability conflict, auth due, canceled-rebook, unfilled. Severity sort; filters by patient,
 * clinician, discipline, territory. No assignees, no claim, no resolve, no
 * escalation: rows clear when their cause clears. The view merges these rows
 * with the product's own worklist warnings.
 */
import type { Discipline, DateKey, DemoState, Hold, IsoString, PlanVisit, VisitResourceLike, WorklistItem } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, addWorkingDaysToKey, daysBetweenKeys, formatInBranchZone, selectDemoNow, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { visitsOnDay } from "./confirmation";
import { AVAILABILITY_ROW_PREFIX, availabilityConflictItem, availabilityRowIsLive } from "./constraints";
import { effectiveVisitsOnDay } from "./effectiveCalendar";
import { reasonLabels, roundFor } from "./confirmationRound";
import { socDueByIso } from "./holds";
import { complianceWindowFor, selectOnPace } from "./lupa";
import { excludedVisitIdsFrom } from "../store/reducer";
import { recertWindow } from "@caredna-marlin/utils/homeHealthDemo/engine/rules";
import { territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { atInstant, atWall, joinSentences, type TimeSentence } from "@caredna-marlin/utils/homeHealthDemo/engine/timeText";

export type WorklistKind =
  | "socDue"
  | "recertWindow"
  | "supervisoryDue"
  | "frequencyShortfall"
  | "lupaAtRisk"
  | "needsCall"
  | "availabilityConflict"
  | "authDue"
  | "canceledRebook"
  | "unfilled";

export type Severity = 1 | 2 | 3;

export interface WorklistRow {
  id: string;
  kind: WorklistKind;
  severity: Severity;
  title: string;
  /** Prose, with a time token wherever a clock time belongs. The view phrases it. */
  reason: TimeSentence;
  patientRef?: string;
  patientName?: string;
  /** The clinician the row is about (the visit's clinician, the hold's clinician). Never an actor. */
  clinicianHandle?: string;
  /** Derived from the clinician or the plan visit — the discipline filter reads it. */
  discipline?: Discipline;
  /** Patient territory — the territory filter reads it. */
  territory?: string;
  dueAtIso?: IsoString;
  countdown?: TimeSentence;
  planVisitId?: string;
  realVisitId?: number;
  itemId?: string;
  episodeId?: string;
  /** Competencies the visit behind the row needs — the coverage drawer filters on them. */
  requiredTraits?: string[];
  source: "hold" | "episode" | "confirmation" | "item" | "callOut" | "visit";
}

export const KIND_LABELS: Record<WorklistKind, string> = {
  socDue: "SOC due",
  recertWindow: "Recert window",
  supervisoryDue: "Supervisory due",
  frequencyShortfall: "Frequency shortfall",
  lupaAtRisk: "Visits at risk",
  needsCall: "Needs a call",
  availabilityConflict: "Availability conflict",
  authDue: "Auth due",
  canceledRebook: "Canceled, rebook",
  unfilled: "Unfilled visit",
};

export function countdownLabel(dueAtIso: IsoString, now: Date, timeZone: string): TimeSentence {
  const ms = new Date(dueAtIso).getTime() - now.getTime();
  const abs = Math.abs(ms);
  const hours = Math.floor(abs / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  const span = hours >= 48 ? `${Math.round(hours / 24)}d` : hours >= 1 ? `${hours}h ${minutes}m` : `${minutes}m`;
  // The weekday is date-only and never moves with the setting; the hour beside it does.
  const day = formatInBranchZone(new Date(dueAtIso), timeZone, "EEE");
  return ms >= 0 ? [`by ${day} `, atInstant(dueAtIso), ` · ${span} left`] : [`${day} `, atInstant(dueAtIso), ` · ${span} overdue`];
}

export function severityForHoursLeft(hoursLeft: number): Severity {
  if (hoursLeft <= 12) return 3;
  if (hoursLeft <= 24) return 2;
  return 1;
}

function patientName(state: Pick<DemoState, "patients">, ref: string | undefined): string | undefined {
  return ref ? state.patients[ref]?.displayName : undefined;
}

function endOfDayIso(dateKey: DateKey): IsoString {
  return new Date(`${dateKey}T17:00:00Z`).toISOString();
}

function holdRows(state: DemoState, now: Date, tz: string): WorklistRow[] {
  const out: WorklistRow[] = [];
  for (const hold of Object.values(state.holds) as Hold[]) {
    if (hold.status !== "held") continue;
    const referral = state.referrals[hold.referralId];
    if (!referral) continue;
    const dueAtIso = socDueByIso(referral, tz);
    const hoursLeft = (new Date(dueAtIso).getTime() - now.getTime()) / 3_600_000;
    out.push({
      id: `row-soc-${hold.id}`,
      kind: "socDue",
      severity: severityForHoursLeft(hoursLeft),
      title: `SOC due · ${patientName(state, hold.patientRef) ?? hold.patientRef}`,
      reason: hold.providerHandle
        ? [`Held ${formatInBranchZone(new Date(`${hold.date}T12:00:00Z`), "UTC", "EEE MMM d")} `, atWall(hold.windowStart), ` on ${state.roster[hold.providerHandle]?.displayName ?? hold.providerHandle}; not yet converted`]
        : // A reservation with no clinician is one the search could not place:
          // there was no free span inside the window on anybody SOC-capable. It
          // is still a reservation — accepting is a person's act and nothing
          // blocks — but it is a row that says what it needs.
          "Needs a time — no free span inside the 48 hours on a SOC-capable clinician",
      patientRef: hold.patientRef,
      patientName: patientName(state, hold.patientRef),
      clinicianHandle: hold.providerHandle,
      dueAtIso,
      countdown: countdownLabel(dueAtIso, now, tz),
      source: "hold",
    });
  }
  return out;
}

function episodeRows(state: DemoState, now: Date, tz: string, today: DateKey): WorklistRow[] {
  const out: WorklistRow[] = [];
  const visits = Object.values(state.planVisits) as PlanVisit[];
  for (const episode of Object.values(state.episodes)) {
    if (episode.background) continue;
    const name = patientName(state, episode.patientRef) ?? episode.patientRef;
    const mine = visits.filter((v) => v.episodeId === episode.id);
    // Recert window approaching (within 7 days) without a real recert visit. The
    // assessment is a reservation now, so the row says which of the two things is
    // true: it is holding a time inside the window and has not been confirmed as
    // a visit, or the search found no free span inside the window at all.
    const rw = recertWindow(episode);
    const recert = mine.find((v) => v.rule === "recert");
    if (recert && recert.status === "placeholder" && daysBetweenKeys(today, rw.start) <= 7 && today <= rw.end) {
      const dueAtIso = endOfDayIso(rw.end);
      const reason: TimeSentence = recert.windowStart
        ? [`Days 56–60 (${rw.start} – ${rw.end}); reserved ${formatInBranchZone(new Date(`${recert.targetDate}T12:00:00Z`), "UTC", "EEE MMM d")} `, atWall(recert.windowStart), ", not yet a visit"]
        : `Days 56–60 (${rw.start} – ${rw.end}); needs a time — no free span inside the window`;
      out.push({ id: `row-recert-${episode.id}`, kind: "recertWindow", severity: today >= rw.start ? 3 : 2, title: `Recert window · ${name}`, reason, patientRef: episode.patientRef, patientName: name, dueAtIso, countdown: countdownLabel(dueAtIso, now, tz), planVisitId: recert.id, episodeId: episode.id, source: "episode" });
    }

    // Supervisory due within 3 days and still a placeholder (standalone) or co-scheduled onto a placeholder.
    for (const v of mine.filter((x) => x.rule === "supervisory" && x.status === "placeholder" && daysBetweenKeys(today, x.targetDate) <= 3 && x.targetDate >= today)) {
      const dueAtIso = endOfDayIso(v.targetDate);
      out.push({ id: `row-sup-${v.id}`, kind: "supervisoryDue", severity: daysBetweenKeys(today, v.targetDate) <= 1 ? 3 : 2, title: `Supervisory due · ${name}`, reason: v.visitTypeKind === "RN_SUPERVISORY" ? "No skilled visit in the 14-day window — standalone RN supervisory needed" : "Co-scheduled onto a skilled visit not yet placed", patientRef: episode.patientRef, patientName: name, dueAtIso, countdown: countdownLabel(dueAtIso, now, tz), planVisitId: v.id, episodeId: episode.id, source: "episode" });
    }

    // Frequency shortfall this week and visits at risk in the period. These carry the consequence of any move; they never name who moved what.
    const pace = selectOnPace(mine, episode, state.config, today);
    const daysLeft = daysBetweenKeys(today, pace.weekEnd);
    if (pace.weekStatus === "behind") {
      const dueAtIso = endOfDayIso(pace.weekEnd);
      out.push({ id: `row-freq-${episode.id}-${pace.weekIndex}`, kind: "frequencyShortfall", severity: daysLeft <= 1 ? 3 : 2, title: `Frequency shortfall · ${name}`, reason: `${pace.weekDone + pace.weekScheduled} of ${pace.weekOrdered} ordered visits this week · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left`, patientRef: episode.patientRef, patientName: name, dueAtIso, countdown: countdownLabel(dueAtIso, now, tz), episodeId: episode.id, source: "episode" });
    }
    if (pace.periodStatus === "atRisk" || pace.periodStatus === "belowThreshold") {
      const dueAtIso = endOfDayIso(pace.periodEnd);
      out.push({ id: `row-lupa-${episode.id}-${pace.period}`, kind: "lupaAtRisk", severity: pace.periodStatus === "belowThreshold" ? 3 : 2, title: `Visits at risk · ${name}`, reason: pace.periodStatus === "belowThreshold" ? `Period ${pace.period + 1} projects ${pace.visitsAtRisk} visit${pace.visitsAtRisk === 1 ? "" : "s"} below its threshold (${pace.lupaThreshold})` : `Period ${pace.period + 1} has a cushion of ${pace.cushion} above its threshold (${pace.lupaThreshold})`, patientRef: episode.patientRef, patientName: name, dueAtIso, countdown: countdownLabel(dueAtIso, now, tz), episodeId: episode.id, source: "episode" });
    }
  }
  return out;
}

/**
 * Needs a call: the confirmation round's own exceptions, after the cutoff. The
 * office sees the same list the clinician does. It leaves the list the moment
 * she confirms it.
 */
/**
 * One row per visit that still needs a call after the cutoff, with the reason, so
 * the office sees what the clinician sees. Rows only: no assignee, no Claim, no
 * Resolve. Nothing appears before the cutoff has passed — the round is the
 * clinician's until then.
 */
function confirmationRows(state: DemoState, now: Date, tz: string): WorklistRow[] {
  const out: WorklistRow[] = [];
  const tomorrow = addWorkingDaysToKey(toDateKey(now, tz), 1);
  for (const vr of visitsOnDay(state.server, tomorrow, tz)) {
    if (vr.providerId === undefined) continue;
    if (!roundFor(state, vr.providerId, tomorrow).cutoffAtIso) continue;
    const a = state.annotations[String(vr.visitId)];
    // Any reply that is not a yes is work, whether the patient asked for another
    // time or said no outright — the round cannot tell those apart and does not
    // pretend to (Pete, 2026-09-24). A silence is not work: an unanswered text
    // goes on reading Sent and raises nothing (Pete, 2026-09-22).
    if (a?.round?.status !== "needsCall") continue;
    out.push({
      id: `row-needscall-${vr.visitId}`,
      kind: "needsCall",
      severity: 2,
      title: `Needs a call · ${vr.patientName}`,
      reason: a.round.reasons.length ? joinSentences(reasonLabels(state, a.round.reasons), " · ") : "The clinician has to reach this patient before tomorrow",
      patientName: vr.patientName,
      clinicianHandle: handleFor(state, vr.providerId),
      realVisitId: vr.visitId,
      dueAtIso: vr.startIso,
      countdown: countdownLabel(vr.startIso, now, tz),
      source: "confirmation",
    });
  }
  return out;
}

const ITEM_KIND: Record<WorklistItem["kind"], WorklistKind> = {
  authDue: "authDue",
  availabilityConflict: "availabilityConflict",
};

/**
 * An availability conflict is a live row only while it is still true and still
 * near. The item is written once, when the constraint lands; the plan moves under
 * it — the clinician drags the visit to another day in 3.2, the plan is versioned,
 * the visit is delivered — so it is re-checked on every read against where the
 * visit actually sits, and it carries the day it sits on rather than the day it
 * was written about. Outside the lead it is a line on the patient's availability
 * report, not a row. Nothing here resolves anything: the row simply stops being
 * produced when its cause stops being true.
 */
function liveAvailabilityItem(state: DemoState, item: WorklistItem, today: DateKey, nowIso: string): WorklistItem | undefined {
  const planVisitId = item.planVisitId ?? (item.id.startsWith(AVAILABILITY_ROW_PREFIX) ? item.id.slice(AVAILABILITY_ROW_PREFIX.length) : undefined);
  if (!planVisitId) return item; // not derived from a plan visit: left exactly as it was written
  const visit = state.planVisits[planVisitId];
  const check = availabilityRowIsLive(state, visit, today);
  if (!check.live) return undefined;
  const patient = item.patientRef ? state.patients[item.patientRef] : undefined;
  if (!patient || !visit) return item;
  const rewritten = availabilityConflictItem(item.patientRef!, patient.displayName, visit, { start: check.placement.targetDate, end: check.placement.targetDate }, nowIso, check.violations.map((v) => v.reason));
  return { ...item, note: rewritten.note, dueAtIso: rewritten.dueAtIso };
}

function itemRows(state: DemoState, now: Date, tz: string, today: DateKey): WorklistRow[] {
  return state.worklistItems
    .filter((t) => !t.resolved)
    .map((t) => (t.kind === "availabilityConflict" ? liveAvailabilityItem(state, t, today, now.toISOString()) : t))
    .filter((t): t is WorklistItem => t !== undefined)
    .map((t) => {
      const kind = ITEM_KIND[t.kind];
      const severity: Severity = kind === "availabilityConflict" ? 2 : t.dueAtIso && new Date(t.dueAtIso) <= now ? 3 : 2;
      return {
        id: `row-item-${t.id}`,
        kind,
        severity,
        title: `${KIND_LABELS[kind]}${t.patientRef ? ` · ${patientName(state, t.patientRef) ?? t.patientRef}` : ""}`,
        reason: t.note,
        patientRef: t.patientRef,
        patientName: patientName(state, t.patientRef),
        dueAtIso: t.dueAtIso,
        countdown: t.dueAtIso ? countdownLabel(t.dueAtIso, now, tz) : undefined,
        itemId: t.id,
        episodeId: t.episodeId,
        planVisitId: t.planVisitId,
        source: "item" as const,
      };
    });
}

function handleFor(state: Pick<DemoState, "meta">, providerId: number | undefined): string | undefined {
  if (providerId === undefined) return undefined;
  return Object.entries(state.meta.bindings.providerIdsByHandle).find(([, id]) => id === providerId)?.[0];
}

function callOutRows(state: DemoState, now: Date, tz: string): WorklistRow[] {
  const c = state.presenter.callOut;
  if (!c) return [];
  const providerId = state.meta.bindings.providerIdsByHandle[c.providerHandle];
  const name = state.roster[c.providerHandle]?.displayName ?? c.providerHandle;
  return effectiveVisitsOnDay(state, c.date, tz)
    .filter((vr) => vr.providerId === providerId && new Date(vr.startIso) >= new Date(c.startedAtIso))
    .map((vr) => {
      const hoursLeft = (new Date(vr.startIso).getTime() - now.getTime()) / 3_600_000;
      // The plan visit behind the real one carries the competency the visit needs
      // and the compliance window **Move within window** offers.
      const planVisit = Object.values(state.planVisits).find((v) => v.realVisitId === vr.visitId);
      const traits = planVisit?.requiredTraits?.length ? planVisit.requiredTraits : undefined;
      return {
        id: `row-unfilled-${vr.visitId}`,
        kind: "unfilled" as const,
        severity: severityForHoursLeft(hoursLeft),
        title: `Unfilled · ${vr.patientName}`,
        reason: [`${name} called out at `, atInstant(c.startedAtIso), `; ${vr.visitTypeName} not yet covered${traits ? ` · needs ${traits.join(", ")}` : ""}`],
        patientName: vr.patientName,
        patientRef: patientRefFor(state, vr.patientId),
        clinicianHandle: c.providerHandle,
        realVisitId: vr.visitId,
        planVisitId: planVisit?.id,
        episodeId: planVisit?.episodeId,
        requiredTraits: traits,
        dueAtIso: vr.startIso,
        countdown: countdownLabel(vr.startIso, now, tz),
        source: "callOut" as const,
      };
    });
}

function patientRefFor(state: Pick<DemoState, "meta">, patientId: number): string | undefined {
  return Object.entries(state.meta.bindings.patientIdsByRef).find(([, id]) => id === patientId)?.[0];
}

/**
 * Canceled, rebook: every canceled real visit on an active store episode whose
 * compliance week or period still has ordered visits to hit. Derived from status
 * only (the horizon carries no reason). Excludes visits the demo created or
 * corrected. Clears when a later visit restores the count.
 */
function canceledRebookRows(state: DemoState, now: Date, tz: string, today: DateKey): WorklistRow[] {
  const out: WorklistRow[] = [];
  const excluded = new Set(excludedVisitIdsFrom(state));
  const seen = new Set<number>();
  const planByReal = new Map<number, PlanVisit>();
  for (const v of Object.values(state.planVisits)) if (v.realVisitId !== undefined) planByReal.set(v.realVisitId, v);
  // A week back: the horizon reads fourteen days behind demo day for the
  // continuity and planned-versus-actual reads, and a cancel older than the
  // compliance week it belongs to is not a row anybody can still work.
  const floor = addDaysToKey(today, -7);
  for (const vr of state.server.visitResources) {
    if (vr.eventStatus !== "canceled" || seen.has(vr.visitId) || excluded.has(vr.visitId)) continue;
    if (toDateKey(new Date(vr.startIso), tz) < floor) continue;
    seen.add(vr.visitId);
    const patientRef = patientRefFor(state, vr.patientId);
    const episode = patientRef ? Object.values(state.episodes).find((e) => e.patientRef === patientRef) : undefined;
    if (!episode) continue;
    const planVisit = planByReal.get(vr.visitId);
    const day = toDateKey(new Date(vr.startIso), tz);
    const mine = Object.values(state.planVisits).filter((v) => v.episodeId === episode.id);
    const pace = selectOnPace(mine, episode, state.config, day > today ? today : day);
    const short = pace.weekStatus === "behind" || pace.periodStatus === "atRisk" || pace.periodStatus === "belowThreshold";
    if (!short) continue;
    const window = planVisit ? complianceWindowFor(planVisit, episode) : { start: pace.weekStart, end: pace.weekEnd };
    const dueAtIso = endOfDayIso(window.end);
    const hoursLeft = (new Date(dueAtIso).getTime() - now.getTime()) / 3_600_000;
    out.push({
      id: `row-canceled-${vr.visitId}`,
      kind: "canceledRebook",
      severity: hoursLeft <= 24 ? 3 : 2,
      title: `Canceled, rebook · ${vr.patientName}`,
      reason: [`${vr.visitTypeName} ${formatInBranchZone(new Date(vr.startIso), tz, "EEE MMM d")} `, atInstant(vr.startIso), ` canceled; ${pace.weekDone + pace.weekScheduled} of ${pace.weekOrdered} ordered this week`],
      patientName: vr.patientName,
      patientRef,
      clinicianHandle: handleFor(state, vr.providerId),
      realVisitId: vr.visitId,
      planVisitId: planVisit?.id,
      episodeId: episode.id,
      dueAtIso,
      countdown: countdownLabel(dueAtIso, now, tz),
      source: "visit",
    });
  }
  return out;
}

export function sortBySeverity(rows: WorklistRow[]): WorklistRow[] {
  return [...rows].sort((a, b) => b.severity - a.severity || (a.dueAtIso ?? "9").localeCompare(b.dueAtIso ?? "9") || a.title.localeCompare(b.title));
}

export interface WorklistFilters {
  patientRef?: string;
  clinicianHandle?: string;
  discipline?: string;
  territory?: string;
  kinds?: WorklistKind[];
}

export function selectWorklistRows(state: DemoState, wallNow: Date = new Date(), filters: WorklistFilters = {}): WorklistRow[] {
  const tz = state.config.branch.timeZone;
  const now = selectDemoNow(state, wallNow);
  const today = toDateKey(now, tz);
  let rows = [
    ...holdRows(state, now, tz),
    ...episodeRows(state, now, tz, today),
    ...confirmationRows(state, now, tz),
    ...itemRows(state, now, tz, today),
    ...callOutRows(state, now, tz),
    ...canceledRebookRows(state, now, tz, today),
  ].map((r) => withTerritory(state, withDiscipline(state, r)));
  if (filters.patientRef) rows = rows.filter((r) => r.patientRef === filters.patientRef);
  if (filters.clinicianHandle) rows = rows.filter((r) => r.clinicianHandle === filters.clinicianHandle);
  if (filters.discipline) rows = rows.filter((r) => r.discipline === filters.discipline);
  if (filters.territory) rows = rows.filter((r) => r.territory === filters.territory);
  if (filters.kinds) rows = rows.filter((r) => filters.kinds!.includes(r.kind));
  return sortBySeverity(rows);
}

function withDiscipline(state: Pick<DemoState, "roster" | "planVisits" | "meta" | "server">, row: WorklistRow): WorklistRow {
  if (row.discipline) return row;
  let discipline: Discipline | undefined;
  if (row.planVisitId) discipline = state.planVisits[row.planVisitId]?.discipline;
  if (!discipline && row.clinicianHandle) discipline = state.roster[row.clinicianHandle]?.discipline;
  if (!discipline && row.realVisitId !== undefined) {
    const vr = state.server.visitResources.find((v) => v.visitId === row.realVisitId);
    const handle = vr ? Object.entries(state.meta.bindings.providerIdsByHandle).find(([, id]) => id === vr.providerId)?.[0] : undefined;
    if (handle) discipline = state.roster[handle]?.discipline;
  }
  return discipline ? { ...row, discipline } : row;
}

function withTerritory(state: Pick<DemoState, "patients" | "config" | "server">, row: WorklistRow): WorklistRow {
  if (row.territory) return row;
  let territory: string | undefined = row.patientRef ? state.patients[row.patientRef]?.territory : undefined;
  if (!territory && row.realVisitId !== undefined) {
    const vr: VisitResourceLike | undefined = state.server.visitResources.find((v) => v.visitId === row.realVisitId);
    territory = territoryForZip(state.config.branch, vr?.patientZip);
  }
  return territory ? { ...row, territory } : row;
}
